import { useEffect, useRef, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import SuccessCheckmark from "./SuccessCheckmark";
import { submitInvestorDeckRequest } from "../utils/investorDeckRequest";
import { trackEvent } from "../utils/analytics";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Status = "idle" | "loading" | "success";

interface InvestorDeckRequestModalProps {
  open: boolean;
  onClose: () => void;
}

export default function InvestorDeckRequestModal({ open, onClose }: InvestorDeckRequestModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  const [name, setName] = useState("");
  const [firm, setFirm] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => firstFieldRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
    // Reset for the next time this is opened, after its close transition.
    const resetId = setTimeout(() => {
      setName("");
      setFirm("");
      setEmail("");
      setReason("");
      setStatus("idle");
    }, 200);
    return () => clearTimeout(resetId);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await submitInvestorDeckRequest({ name, firm, email, reason });
      trackEvent("investor_deck_request_submit");
      setStatus("success");
    } catch {
      setStatus("idle");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.15 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ocean-950/70 px-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Request Investor Deck"
            initial={reducedMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl dark:bg-ocean-900"
          >
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-lg font-semibold text-ocean-950 dark:text-foam-50">Request Investor Deck</h3>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ocean-800/50 hover:bg-ocean-900/5 dark:text-foam-100/50 dark:hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {status === "success" ? (
              <div role="status" aria-live="polite" className="mt-6 rounded-2xl bg-teal-400/10 p-6 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-teal-400/20 text-teal-600">
                  <SuccessCheckmark className="h-5 w-5" />
                </div>
                <h4 className="mt-3 font-display font-semibold text-ocean-950 dark:text-foam-50">Request received</h4>
                <p className="mt-2 text-sm text-ocean-800/70 dark:text-foam-100/70">
                  Thanks — our founders/BD team will follow up by email shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Your name</span>
                  <input
                    ref={firstFieldRef}
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Firm / fund</span>
                  <input
                    required
                    type="text"
                    value={firm}
                    onChange={(e) => setFirm(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Email</span>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-ocean-900 dark:text-foam-100">Reason for interest</span>
                  <textarea
                    required
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1.5 w-full resize-none rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
                  />
                </label>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-50"
                >
                  {status === "loading" ? "Sending…" : "Send Request"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
